import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';

import videoService, { OpenRoom } from '../services/videoService';
import { formatDateTime, formatTime } from '../utils/dateUtils';
import { useVideoCall } from '../contexts/VideoCallContext';

/**
 * Every video room currently open across the tenant.
 *
 * Gated behind the view_video_rooms privilege, because it shows sessions the
 * viewer is not necessarily running - which is the point (covering reception,
 * supervising, seeing a patient sitting alone) but is not for everyone.
 */

const REFRESH_MS = 15000;

const VideoRoomsPage: React.FC = () => {
  const [rooms, setRooms] = useState<OpenRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { startCall } = useVideoCall();

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      setRooms(await videoService.listOpenRooms());
      setError(null);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not load open rooms.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
    // Rooms open and close without any action on this page, so a stale table is
    // worse than useless - someone would think a patient is still waiting.
    const id = setInterval(() => load(false), REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const join = (room: OpenRoom) => {
    const meetingId = Number(room.external_id);
    if (!Number.isFinite(meetingId)) {
      toast.error('This room belongs to another system and cannot be joined from here.');
      return;
    }
    startCall(meetingId);
  };

  const copyLink = async (room: OpenRoom) => {
    try {
      await navigator.clipboard.writeText(room.join_url);
      toast.success('Patient link copied');
    } catch {
      window.prompt('Copy the patient link:', room.join_url);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" sx={{ mb: 1, color: '#366A75', fontWeight: 600 }}>
        Open video rooms
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: '#5B7F8A' }}>
        Sessions running right now. Refreshes automatically.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : rooms.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', color: '#5B7F8A' }}>
          No video rooms are open at the moment.
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Patient</TableCell>
                <TableCell>Therapist</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Patient status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rooms.map((room) => (
                <TableRow key={room.session_id} hover>
                  <TableCell>{room.patient_display_name || '—'}</TableCell>
                  <TableCell>{room.therapist_display_name || '—'}</TableCell>
                  <TableCell>{formatDateTime(room.started_at, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</TableCell>
                  <TableCell>
                    {room.patient_joined_at ? (
                      <Chip size="small" color="success" label={`In room · ${formatTime(room.patient_joined_at)}`} />
                    ) : (
                      // The case worth spotting from across the clinic: the room is
                      // open but nobody has arrived.
                      <Chip size="small" variant="outlined" label="Not joined" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => copyLink(room)} sx={{ mr: 1 }}>
                      Copy link
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => join(room)}
                      sx={{ backgroundColor: '#3A7380', '&:hover': { backgroundColor: '#2D5A60' } }}
                    >
                      Join
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default VideoRoomsPage;
